import { action } from 'mobx';
import i18next from 'i18next';

import { AppManager, Variables } from 'core/index';
import { getWhitelabelConfig } from 'core/whitelabel';

import { ProjectConnManager } from 'apps/project/connManager';
import { IssueConnManager } from 'apps/issueTracker/appManager';
import { ThreadConnManager } from 'apps/forum/main/appManager';
import { ProfileConnManager } from 'apps/profile/main/connManager';
import { InitiativeConnManager } from 'apps/initiative/connManager';
import { StoryConnManager } from 'apps/story/main/appManager';

import {
  FeaturedProjectsQuery,
  FeaturedStoriesQuery,
} from 'apps/home/featured/gql';

import {
  WhiteLabelProjectsQuery,
  WhiteLabelIssuesQuery,
  WhiteLabelThreadsQuery,
  WhiteLabelProfilesQuery,
  WhiteLabelInitiativesQuery,
  WhiteLabelStoriesQuery,
  ViralIssuesQuery,
} from 'apps/microSite/gql';

const contentManagers = {
  Project: ProjectConnManager,
  Issue: IssueConnManager,
  ViralIssue: IssueConnManager,
  Thread: ThreadConnManager,
  Profile: ProfileConnManager,
  Initiative: InitiativeConnManager,
  Story: StoryConnManager,
};

const featuredContentQueries = {
  Project: FeaturedProjectsQuery,
  Story: FeaturedStoriesQuery,
};

const contentQueries = {
  Project: WhiteLabelProjectsQuery,
  Issue: WhiteLabelIssuesQuery,
  ViralIssue: ViralIssuesQuery,
  Thread: WhiteLabelThreadsQuery,
  Profile: WhiteLabelProfilesQuery,
  Initiative: WhiteLabelInitiativesQuery,
  Story: WhiteLabelStoriesQuery,
};

const contentSorting = {
  Project: {
    default: 'discover_recent',
    options: [
      ['discover_recent', i18next.t('Recent')],
      ['followers', i18next.t('Followers')],
      ['contributions', i18next.t('Contributions')],
      ['pageviews', i18next.t('Views')],
    ],
  },
  Issue: {
    default: 'recent',
    options: [
      ['recent', i18next.t('Recent')],
      ['comments', i18next.t('Comments')],
    ],
  },
  ViralIssue: {
    default: 'recent',
    options: [
      ['recent', i18next.t('Recent')],
      ['comments', i18next.t('Comments')],
    ],
  },
  Thread: {
    default: 'recent',
    options: [
      ['recent', i18next.t('Recent')],
      ['likes', i18next.t('Likes')],
      ['comments', i18next.t('Comments')],
    ],
  },
  Profile: {
    default: 'recent',
    options: [
      ['recent', i18next.t('Recent')],
      ['followers', i18next.t('Followers')],
    ],
  },
  Initiative: {
    default: 'recent',
    options: [
      ['recent', i18next.t('Recent')],
      ['followers', i18next.t('Followers')],
    ],
  },
  Story: {
    default: 'recent',
    options: [
      ['recent', i18next.t('Recent')],
      ['-likes', i18next.t('Likes')],
      ['pageviews', i18next.t('Views')],
    ],
  },
};

export class MicroSitePageManager extends AppManager {
  constructor(parent, init = {}) {
    super(parent, init);
    this.whitelabelConfig = getWhitelabelConfig();
    this.pageConfig = this.whitelabelConfig.pages[this.route.name];

    this.variables = new Variables({});
    this.sectionVariables = {};
    const featuredSectionIds = [];
    this.pageConfig.sections.forEach(section => {
      if (section.featuredIn) {
        featuredSectionIds.push(section.featuredIn);
      }
    });
    this.pageConfig.sections.forEach(section => {
      const key = `${section.name.replace(/\s/g, '').toLowerCase()}ConnManager`;
      if (section.featuredIn) {
        this[key] = new contentManagers[section.content](this, {
          contains: ['featured_in', section.featuredIn],
          sortBy: contentSorting[section.content].default,
          sortOptions: contentSorting[section.content].options,
          initialBatchSize: 6,
          loadMoreQuery: featuredContentQueries[section.content],
        });
        this.sectionVariables[`featured${section.content}sCursor`] = () =>
          this[key].endCursor;
        this.sectionVariables[`featured${section.content}sSortBy`] = () =>
          this[key].sortBy;
        this.sectionVariables[`featured${section.content}sCount`] = () =>
          this[key].variables.batchSize;
      } else {
        this[key] = new contentManagers[section.content](this, {
          whitelabels:
            section.content === 'Profile'
              ? [this.whitelabelConfig.id, null]
              : [this.whitelabelConfig.id],
          sortBy: contentSorting[section.content].default,
          sortOptions: contentSorting[section.content].options,
          initialBatchSize: 12,
          loadMoreQuery: contentQueries[section.content],
          filterBy: ['Issue', 'ViralIssue'].includes(section.content)
            ? [['TrackerIssue.status', 'Open']]
            : null,
          notContains:
            section.content === 'Thread'
              ? [['forumId', '53']] // FIXME HACK
              : [['featured_in', ...featuredSectionIds]],
        });
        this.sectionVariables[`wl${section.content}sCursor`] = () =>
          this[key].endCursor;
        this.sectionVariables[`wl${section.content}sSortBy`] = () =>
          this[key].sortBy;
        this.sectionVariables[`wl${section.content}sCount`] = () =>
          this[key].variables.batchSize;
      }
    });
    this.variables = new Variables(this.sectionVariables);
    this.MicroSitePageQuery();
  }

  @action MicroSitePageQuery = () => {
    this.pageConfig.sections.forEach(section => {
      const sectionKey = `${section.name
        .replace(/\s/g, '')
        .toLowerCase()}ConnManager`;
      if (this[sectionKey]) {
        this[sectionKey].loadMore();
      }
    });
  };
}
